'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useStrategies from './useStrategies';
import useScoutContext from 'src/hooks/scout/useScoutContext';
import toScoutSelection from 'src/hooks/scout/toScoutSelection';
import { SCOUT_CONTEXT_KEYS } from 'src/hooks/scout/constants';
import { useLogging } from 'src/context/logging';
import {
  useLazyGetAllStrategiesMembersQuery,
  AllStrategiesMembersParams,
} from 'src/api/redux/strategy/allStrategiesApi';

// Largest page the members endpoint will return (backend max_page_size).
// Requesting more is clamped server-side, so this is the drain page size.
const MEMBERS_PAGE_SIZE = 1000;

// Safety ceiling on drain iterations when totalCount is unknown, so a
// malformed `next` can never spin forever (200 pages = 200k providers).
const MAX_SELECT_ALL_PAGES = 200;

/**
 * Manages "select all" with a Gmail-style two-step pattern:
 *
 * 1. Click header checkbox → selects only currently loaded items, banner appears
 * 2. Click "Select all {totalCount} opportunities" → instantly sets isAllSelected,
 *    fires a background drain that pages through the ENTIRE result set
 * 3. Uncheck any item → banner disappears, reverts to manual selection
 *
 * Selection is always instant. The full provider fetch runs in the background
 * so the UI never blocks. `effectiveProviders` falls back to already-loaded
 * items while the fetch is in-flight, and swaps to the full set when ready.
 * `isResolvingSelection` is true while the drain runs so callers can block the
 * add-to-list action until the complete set is available (otherwise a fast
 * click would add only the loaded page).
 */
const useSelectAll = (
  location: string,
  allResults: MedScout.Opportunity[],
  totalCount?: number,
  queryParams?: Partial<AllStrategiesMembersParams>,
  hasNextPage?: boolean
) => {
  const {
    selectedProviders,
    setSelectedProviders,
    isAllSelected,
    setIsAllSelected,
  } = useStrategies(location);

  const log = useLogging();
  const [triggerFetchAll] = useLazyGetAllStrategiesMembersQuery();

  // Track all fetched providers when "select all opportunities" is used.
  // Empty while background fetch is in-flight; populated when it completes.
  const [allFetchedProviders, setAllFetchedProviders] = useState<
    MedScout.Opportunity[]
  >([]);

  // True while the background drain is paging through the full result set.
  // Consumers gate the add-to-list action on this so the complete selection
  // is sent, not just the first page.
  const [isResolvingSelection, setIsResolvingSelection] = useState(false);

  // True when the user selected via the header checkbox (bulk mode).
  // In this mode, new items loaded via infinite scroll are auto-added
  // to the selection so the checkbox stays checked and the banner persists.
  const [isBulkSelected, setIsBulkSelected] = useState(false);

  // Generation counter to invalidate stale background fetches.
  // Incremented on every selectAll, checked when fetch resolves —
  // if the generation has changed (reset/deselect happened), the
  // callback is a no-op.
  const fetchGenerationRef = useRef(0);

  // Derive the effective list of selected providers.
  // When isAllSelected: use the full fetched set if available, otherwise
  // fall back to allResults (already-loaded items) for instant feedback.
  const effectiveProviders = useMemo(() => {
    if (isAllSelected) {
      return allFetchedProviders.length > 0 ? allFetchedProviders : allResults;
    }
    return selectedProviders || [];
  }, [isAllSelected, allFetchedProviders, allResults, selectedProviders]);

  // Count to display in action buttons when isAllSelected.
  // Uses totalCount (API truth) rather than effectiveProviders.length
  // to avoid showing the dedup-artifact count.
  const effectiveCount = useMemo(() => {
    if (isAllSelected) {
      return totalCount ?? effectiveProviders.length;
    }
    return effectiveProviders.length;
  }, [isAllSelected, totalCount, effectiveProviders.length]);

  // Share the selection (IDs and count only) with Scout. Registered here so
  // Strategy Center's list and map views share one entry; the count covers
  // "select all" even before every ID has loaded.
  const scoutSelection = useMemo(
    () =>
      toScoutSelection(
        effectiveProviders.map((provider) => provider?.provider_id),
        effectiveCount
      ),
    [effectiveProviders, effectiveCount]
  );
  useScoutContext(
    SCOUT_CONTEXT_KEYS.STRATEGY_SELECTION,
    'Selected Strategy Center providers',
    scoutSelection
  );

  // Auto-include newly loaded items when in bulk-select mode.
  // When the user clicked the header checkbox, scrolling down and loading
  // more items should keep them selected rather than breaking the selection.
  const prevResultsLengthRef = useRef(allResults.length);
  useEffect(() => {
    if (
      isBulkSelected &&
      !isAllSelected &&
      allResults.length > prevResultsLengthRef.current
    ) {
      setSelectedProviders(allResults);
    }
    prevResultsLengthRef.current = allResults.length;
  }, [allResults, isBulkSelected, isAllSelected, setSelectedProviders]);

  // Header checkbox: checked when all loaded items are selected
  const selectedSet = useMemo(
    () => new Set((selectedProviders || []).map((p) => p.provider_id)),
    [selectedProviders]
  );

  const isHeaderChecked = useMemo(() => {
    if (allResults.length === 0) return false;
    if (isAllSelected) return true;
    return allResults.every((r) => selectedSet.has(r.provider_id));
  }, [allResults, isAllSelected, selectedSet]);

  // Banner state logic
  const bannerState = useMemo<'page-selected' | 'all-selected' | null>(() => {
    if (isAllSelected) return 'all-selected';
    if (
      isHeaderChecked &&
      allResults.length > 0 &&
      hasNextPage &&
      totalCount != null &&
      totalCount > allResults.length
    ) {
      return 'page-selected';
    }
    return null;
  }, [
    isAllSelected,
    isHeaderChecked,
    allResults.length,
    hasNextPage,
    totalCount,
  ]);

  const showBanner = bannerState !== null;
  const loadedCount = allResults.length;

  // Drain the full set in the background by paging through every page until
  // there's no `next`. Does not block — UI updates instantly via the
  // isAllSelected flag and falls back to loaded items until this completes.
  // Uses the generation counter to discard results (and avoid clearing the
  // resolving flag) if the selection was reset/cleared mid-drain.
  const fetchAllInBackground = useCallback(
    async (params: Partial<AllStrategiesMembersParams>) => {
      const generation = ++fetchGenerationRef.current;
      setIsResolvingSelection(true);

      // Bound iterations by the known total (+1 for off-by-one safety) so a
      // malformed `next` can't loop forever; fall back to a hard ceiling.
      const maxPages =
        totalCount != null && totalCount > 0
          ? Math.ceil(totalCount / MEMBERS_PAGE_SIZE) + 1
          : MAX_SELECT_ALL_PAGES;

      try {
        // Dedupe by provider_id while collecting — offset pagination can return
        // overlapping rows across pages (mirrors the `allResults` flatteners).
        const collected: MedScout.Opportunity[] = [];
        const seen = new Set<string>();
        let complete = false;

        for (let page = 1; page <= maxPages; page++) {
          const result = await triggerFetchAll({
            ...params,
            pageSize: MEMBERS_PAGE_SIZE,
            page,
          }).unwrap();

          // Stale drain — selection was reset since this drain started.
          // Bail without committing; a newer drain now owns the flag.
          if (fetchGenerationRef.current !== generation) return;

          for (const r of result?.results || []) {
            const pid = r.provider_id || r.id;
            if (!seen.has(pid)) {
              seen.add(pid);
              collected.push({ ...r, provider_id: pid } as MedScout.Opportunity);
            }
          }

          if (!result?.next) {
            complete = true;
            break;
          }
        }

        // Only commit a set we know is complete. Exhausting the safety ceiling
        // without reaching the last page means the set is partial — treat it as
        // a failure rather than silently marking a truncated selection resolved.
        if (!complete) {
          throw new Error(
            `Strategy select-all drain did not complete within ${maxPages} pages`
          );
        }

        setAllFetchedProviders(collected);
        setSelectedProviders(collected);
      } catch (err) {
        // Drain failed/incomplete — revert to manual (loaded-page) selection so
        // the banner no longer claims the full set is selected and any add uses
        // a real, complete set rather than a silent partial.
        if (fetchGenerationRef.current === generation) {
          setIsAllSelected(false);
          setAllFetchedProviders([]);
          setSelectedProviders(allResults);
        }
        log.exception(err, { tags: { context: 'strategy-select-all-drain' } });
      } finally {
        // Only clear the flag if this drain is still the active one — a newer
        // drain that superseded this one manages its own flag lifecycle.
        if (fetchGenerationRef.current === generation) {
          setIsResolvingSelection(false);
        }
      }
    },
    [
      triggerFetchAll,
      setSelectedProviders,
      setIsAllSelected,
      allResults,
      totalCount,
      log,
    ]
  );

  // Select all opportunities — sets flag instantly, fetches in background.
  // Used by both the banner link and the header checkbox (when all pages loaded).
  const selectAll = useCallback(() => {
    if (!queryParams) return;
    // Instant: flag + select loaded items as placeholder
    setIsAllSelected(true);
    setSelectedProviders(allResults);
    // Background: drain the full set (fire-and-forget; it manages its own state)
    void fetchAllInBackground(queryParams);
  }, [
    queryParams,
    allResults,
    setIsAllSelected,
    setSelectedProviders,
    fetchAllInBackground,
  ]);

  // Step 1: Toggle header checkbox
  const handleToggleSelectAll = useCallback(() => {
    if (isHeaderChecked) {
      // Deselect all — bump generation to invalidate any in-flight drain
      fetchGenerationRef.current++;
      setIsAllSelected(false);
      setIsBulkSelected(false);
      setSelectedProviders([]);
      setAllFetchedProviders([]);
      setIsResolvingSelection(false);
    } else if (!hasNextPage && queryParams) {
      // All pages loaded — skip banner, go straight to all-selected
      setIsBulkSelected(true);
      selectAll();
    } else {
      // More pages to load — select only loaded items (step 1)
      // Enable bulk mode so new items from infinite scroll are auto-added
      setIsAllSelected(false);
      setIsBulkSelected(true);
      setSelectedProviders(allResults);
    }
  }, [
    isHeaderChecked,
    hasNextPage,
    allResults,
    queryParams,
    setIsAllSelected,
    setSelectedProviders,
    selectAll,
  ]);

  // Step 2: Banner link "Select all {totalCount} opportunities"
  const handleSelectAllOpportunities = selectAll;

  // Select first N opportunities — fetches up to N providers from the API
  const handleSelectFirstN = useCallback(
    async (count: number) => {
      if (!queryParams) return;

      const generation = ++fetchGenerationRef.current;
      setIsResolvingSelection(true);
      setIsAllSelected(false);
      setIsBulkSelected(false);

      // Use loaded items as immediate placeholder
      const immediateSlice = allResults.slice(0, count);
      setSelectedProviders(immediateSlice);

      try {
        const collected: MedScout.Opportunity[] = [];
        const seen = new Set<string>();
        const maxPages = Math.ceil(count / MEMBERS_PAGE_SIZE) + 1;

        for (let page = 1; page <= maxPages; page++) {
          const result = await triggerFetchAll({
            ...queryParams,
            pageSize: Math.min(count, MEMBERS_PAGE_SIZE),
            page,
          }).unwrap();

          if (fetchGenerationRef.current !== generation) return;

          for (const r of result?.results || []) {
            const pid = r.provider_id || r.id;
            if (!seen.has(pid)) {
              seen.add(pid);
              collected.push({ ...r, provider_id: pid } as MedScout.Opportunity);
            }
            if (collected.length >= count) break;
          }

          if (collected.length >= count || !result?.next) break;
        }

        if (fetchGenerationRef.current === generation) {
          setSelectedProviders(collected);
          setAllFetchedProviders([]);
        }
      } catch (err) {
        if (fetchGenerationRef.current === generation) {
          // Keep whatever was loaded as selection
          setSelectedProviders(allResults.slice(0, count));
        }
        log.exception(err, { tags: { context: 'strategy-select-first-n' } });
      } finally {
        if (fetchGenerationRef.current === generation) {
          setIsResolvingSelection(false);
        }
      }
    },
    [
      queryParams,
      allResults,
      triggerFetchAll,
      setIsAllSelected,
      setSelectedProviders,
      log,
    ]
  );

  // Unified handler for individual row toggling
  const handleToggleProvider = useCallback(
    (providerId: string, provider?: MedScout.Opportunity) => {
      if (isAllSelected) {
        // Revert to manual mode: keep all items minus unchecked one
        const source =
          allFetchedProviders.length > 0 ? allFetchedProviders : allResults;
        const remaining = source.filter((p) => p.provider_id !== providerId);
        fetchGenerationRef.current++;
        setIsAllSelected(false);
        setIsBulkSelected(false);
        setAllFetchedProviders([]);
        setSelectedProviders(remaining);
        setIsResolvingSelection(false);
        return;
      }

      const currentProviders = selectedProviders || [];
      const isCurrentlySelected = currentProviders.some(
        (p) => p.provider_id === providerId
      );

      if (isCurrentlySelected) {
        // Unchecking an item exits bulk mode
        setIsBulkSelected(false);
        setSelectedProviders(
          currentProviders.filter((p) => p.provider_id !== providerId)
        );
      } else if (provider) {
        setSelectedProviders([...currentProviders, provider]);
      }
    },
    [
      isAllSelected,
      allFetchedProviders,
      allResults,
      selectedProviders,
      setIsAllSelected,
      setSelectedProviders,
    ]
  );

  // Reset selection state (call when filters/search/sort change).
  // Bumps generation to invalidate any in-flight background fetch.
  const resetSelection = useCallback(() => {
    fetchGenerationRef.current++;
    setIsAllSelected(false);
    setIsBulkSelected(false);
    setSelectedProviders([]);
    setAllFetchedProviders([]);
    setIsResolvingSelection(false);
  }, [setIsAllSelected, setSelectedProviders]);

  return {
    effectiveProviders,
    effectiveCount,
    isAllSelected,
    isResolvingSelection,
    isHeaderChecked,
    handleToggleSelectAll,
    resetSelection,
    showBanner,
    bannerState,
    handleSelectAllOpportunities,
    handleSelectFirstN,
    handleToggleProvider,
    loadedCount,
  };
};

export default useSelectAll;
